import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { GetApp, NavigateNext } from "@material-ui/icons";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import MomentUtils from "@date-io/moment";
import moment from "moment";
import axios from "axios";
import * as XLSX from "xlsx";
import { appUrl } from "../../../../../utils/config";

/*
  BCM-881 — Custom Report Generation.

  Opened by "Custom Report" on the Reconciliation of Inventory page. The user
  picks a single day or a period, each date is resolved to the reconciliation
  run it should report on, and the report is previewed before it is
  downloaded.

    · fetchInventoryReconciliationHistory — given the date (startDate) or the
      period (reportStartDate, reportEndDate), resolves each date to a run and
      returns the message to show for it. The FSD's rules — exact match, the
      run immediately before, the 14-day limit — are applied by the API.
      Called on Continue.
    · fetchInventoryReconciliationData, given a run's
      collabrixReconciliationID — the rows of that run, as they stood. Called
      on Continue, once for every run the dates resolved to.
*/


const apiBase = () =>
  `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}`;

/*
  The four BMS SAP inventory statuses, carried in the report as individual
  columns against every batch whether or not they hold a quantity, in the
  order the FSD gives, under the fetchInventoryReconciliationData field names.
*/
const BMS_STATUS_COLUMNS = [
  { key: "sponsorUnrestrictedQty", label: "Unrestricted Stock" },
  { key: "sponsorQualityInspectionQty", label: "Quality Inspection Stock" },
  { key: "sponsorBlockedQty", label: "Blocked Stock" },
  { key: "sponsorRestrictedQty", label: "Restricted Stock" },
];

const useStyles = makeStyles(() => ({
  dialogPaper: {
    borderRadius: "16px",
  },
  segment: {
    display: "inline-flex",
    border: "1px solid #c7c7c7",
    borderRadius: "4px",
    overflow: "hidden",
  },
  segmentButton: {
    textTransform: "none",
    borderRadius: 0,
    padding: "8px 20px",
    fontSize: "0.9rem",
    boxShadow: "none",
    "&:hover": { boxShadow: "none" },
  },
  segmentOn: {
    backgroundColor: "#241b52",
    color: "#fff",
    fontWeight: 600,
    "&:hover": { backgroundColor: "#241b52" },
  },
  segmentOff: {
    backgroundColor: "#fff",
    color: "#241b52",
    "&:hover": { backgroundColor: "#f5f5f5" },
  },
  fieldLabel: {
    fontWeight: 500,
    fontSize: "0.9rem",
    marginBottom: "6px",
  },
  notice: {
    backgroundColor: "#fdf6ec",
    border: "1px solid #f2e3c9",
    borderRadius: "6px",
    padding: "12px 14px",
  },
  noticeHead: {
    fontWeight: 500,
    fontSize: "0.9rem",
    color: "#7a4a12",
  },
  noticeBody: {
    fontSize: "0.9rem",
    color: "#7a4a12",
  },
  continueButton: {
    borderRadius: "2rem",
    textTransform: "none",
    backgroundColor: "#ec6535",
    color: "#fff",
    padding: "8px 24px",
    fontWeight: 500,
    "&:hover": { backgroundColor: "#d4552a" },
    "&.Mui-disabled": { backgroundColor: "#f4b39c", color: "#fff" },
  },
  downloadButton: {
    borderRadius: "6px",
    textTransform: "none",
    backgroundColor: "#286acf",
    color: "#fff",
    padding: "10px 20px",
    fontWeight: 500,
    whiteSpace: "nowrap",
    "&:hover": { backgroundColor: "#1d50a0" },
  },
  // A report row: white cells, tinted on hover — the sticky first cell too,
  // which needs a solid background so the columns scroll beneath it.
  reportRow: {
    "& > td": { backgroundColor: "#fff" },
    "&:hover > td": { backgroundColor: "#eef3fb" },
  },
  stickyFirstCell: {
    position: "sticky",
    left: 0,
    zIndex: 1,
    borderRight: "1px solid #e0e0e0",
  },
  // Covers the selection while the reconciliation runs load.
  loadingOverlay: {
    position: "absolute",
    inset: 0,
    zIndex: 2,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255, 255, 255, 0.85)",
    borderRadius: "8px",
  },
  previewCard: {
    border: "1px solid #e0e0e0",
    borderRadius: "6px",
    overflow: "hidden",
  },
  previewHead: {
    backgroundColor: "#fafafa",
    borderBottom: "1px solid #e0e0e0",
    padding: "12px 16px",
    display: "flex",
    alignItems: "baseline",
    gap: "12px",
  },
  previewFoot: {
    backgroundColor: "#fafafa",
    borderTop: "1px solid #e0e0e0",
    padding: "10px 16px",
    fontSize: "0.85rem",
    color: "#757575",
  },
  backLink: {
    color: "#286acf",
    textDecoration: "underline",
    cursor: "pointer",
    fontSize: "0.9rem",
    whiteSpace: "nowrap",
  },
}));

/* ------------------------------------------------------------------ */
/* reconciliation row helpers — shared with the reconciliation page    */
/* ------------------------------------------------------------------ */

// A reconciliation table quantity.
export const formatReconQty = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  if (!Number.isFinite(num)) return "";
  // Three decimals throughout, as the FSD rounds quantities and the PPT shows
  // them: 8721.000, 6.928, 0.000.
  return num.toFixed(3);
};

/*
  DD-MON-YYYY, e.g. 07-SEP-2026. Built by hand: the en-GB locale abbreviates
  September as "Sept" in current browsers, which read 07-SEPT-2026.
*/
const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

export const formatReconDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return `${String(d.getDate()).padStart(2, "0")}-${MONTHS[d.getMonth()]}-${d.getFullYear()}`;
};

/*
  The FSD excludes a batch from the reconciliation only where both BMS total
  qty. and CMO total qty. are zero.
*/
export const isZeroZeroRow = (row) =>
  Number(row?.sponsorOnHandQty || 0) === 0 && Number(row?.cmoOnHandQty || 0) === 0;

/*
  The CMO batches held against one BMS batch. BCM-881 returns them as
  multipleCMOBatchMapping[]; before it, a row carried a single cmoBatchNumber
  and nothing to expand.
*/
export const cmoBatchesOf = (row) => {
  const list = row?.multipleCMOBatchMapping;
  if (Array.isArray(list) && list.length > 0) return list;
  if (row?.cmoBatchNumber) return [row];
  return [];
};

// A CMO batch's number, under the mapping's name or the flat row's.
const cmoBatchNumberOf = (batch) => batch?.cmoInventoryBatchNumber || batch?.cmoBatchNumber || "";

/*
  Where a CMO batch has no matching BMS batch, the FSD shows the material code,
  description and UoM the CMO reported, each followed by "(CMO)" — a marker
  the API puts on the values it sends.
*/
export const materialCodeCell = (row) => {
  // Descriptions can arrive with stray whitespace, e.g. a trailing tab.
  const clean = (value) => String(value ?? "").trim();
  const matched = !!row?.sponsorBatchNumber;
  if (matched) {
    const code = clean(row.sponsorMaterialCode);
    const desc = clean(row.sponsorMaterialDescription);
    return code && desc ? `${code} / ${desc}` : code || desc;
  }
  // The API marks the CMO's values "(CMO)" itself, so nothing is added here.
  const code = clean(row?.cmoMaterialCode) || clean(row?.sponsorMaterialCode);
  const desc = clean(row?.cmoMaterialDescription) || clean(row?.sponsorMaterialDescription);
  return code && desc ? `${code} / ${desc}` : code || desc;
};

// CMO ID: the SAP vendor number of the CMO the stock is held against.
export const cmoIdCell = (row) => row?.vendorNumber || "";

/*
  CMO Total Qty. and BMS UoM are shown exactly as the API formats them:
  cmoOnHandQtyDisplay carries the CMO's unit where the quantity could not be
  converted, e.g. "2000 (KG)"; sponsorUOMDisplay carries the CMO's UoM marked
  "(CMO)" where no BMS batch matched, e.g. "KG (CMO)".
*/
export const cmoQtyCell = (row) => row?.cmoOnHandQtyDisplay ?? "";

export const uomCell = (row) => row?.sponsorUOMDisplay ?? "";

/* ------------------------------------------------------------------ */
/* date helpers                                                        */
/* ------------------------------------------------------------------ */

const startOfDay = (value) => {
  if (!value) return null;
  // A bare yyyy-mm-dd from a date input is read as local, not UTC, midnight.
  const d =
    typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? new Date(`${value}T00:00:00`)
      : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
};

// A run's calendar day, read off the text of reconciliationDateTimeStamp.
const runDayOf = (timestamp) => {
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(String(timestamp || ""));
  return match ? match[1] : "";
};

const formatDate = (value) => formatReconDate(startOfDay(value));

// The CMO status quantities of a row, from quantityBasedOnStatus[] on the row.
const statusEntriesOf = (row) =>
  Array.isArray(row?.quantityBasedOnStatus) ? row.quantityBasedOnStatus : [];

/*
  The CMO's inventory statuses: one column for each status the rows'
  quantityBasedOnStatus[] entries name, in the order first met.
*/
const cmoStatusColumnsOf = (rows) => {
  const seen = new Map();
  (rows || []).forEach((row) => {
    statusEntriesOf(row).forEach((entry) => {
      const label = entry?.renamedColumnName || entry?.statusName;
      if (label && !seen.has(label)) {
        // partOfCMOTotalQty: whether the status is part of CMO total qty.
        seen.set(label, { key: label, label, partOfCMOTotalQty: !!entry.partOfCMOTotalQty });
      }
    });
  });
  return Array.from(seen.values());
};

/*
  A CMO status quantity for a row, shown as the API formats it: the
  quantityDisplay of that status's quantityBasedOnStatus entry.
*/
const cmoStatusDisplay = (row, label) => {
  // A row that reports its statuses but not this one holds none of it: the
  // FSD carries every configured status "irrespective of whether the status
  // holds a quantity".
  if (!Array.isArray(row?.quantityBasedOnStatus)) return "";
  const entry = row.quantityBasedOnStatus.find(
    (e) => (e?.renamedColumnName || e?.statusName) === label
  );
  return entry ? entry.quantityDisplay ?? "" : "0";
};

/*
  A report quantity as a number, or undefined where the response does not
  carry it — blank, never a made-up zero.
*/
const toAmount = (value) => {
  if (value === null || value === undefined || value === "") return undefined;
  const num = Number(value);
  return Number.isFinite(num) ? num : undefined;
};

// The report shows every quantity to three decimals, as slides 19-21 do.
const formatReportQty = (value) => {
  const num = toAmount(value);
  return num === undefined ? "" : num.toFixed(3);
};

/* ------------------------------------------------------------------ */

function CustomReportDialog(props) {
  const {
    open,
    onClose,
    cmoName,
    brandOwnerMosymphonyID,
    vendorMosymphonyID,
  } = props;
  const classes = useStyles();

  const [mode, setMode] = useState("day"); // "day" | "period"
  const [singleDate, setSingleDate] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  // Set when Continue is pressed without the date(s) the mode needs.
  const [missingDate, setMissingDate] = useState(false);

  /*
    The selected date(s) as fetchInventoryReconciliationHistory resolves them:
    one entry per date, each with the run it resolves to (or none) and the
    message to show for it, plus the message for both dates resolving to one
    report.
  */
  const [resolved, setResolved] = useState(null); // { entries, sameReportMessage }
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState(null);

  // The report rows of each selected run, keyed by collabrixReconciliationID.
  const [reports, setReports] = useState({});
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsError, setReportsError] = useState(null);

  /* ---- the selected dates, resolved by the API --------------------- */

  /*
    The request for the dates selected: a single day sends startDate; a period
    sends reportStartDate and reportEndDate. Null until the mode's date(s) are
    entered.
  */
  const historyRequest = () =>
    mode === "day"
      ? singleDate
        ? { startDate: singleDate }
        : null
      : fromDate && toDate
        ? { reportStartDate: fromDate, reportEndDate: toDate }
        : null;

  /*
    fetchInventoryReconciliationHistory resolves the dates to reconciliation
    runs and says what to tell the user — exact match, the earlier report used
    instead, beyond the 14-day limit, both dates on one report.
  */
  const resolveDates = async (request) => {
    const res = await axios.post(`${apiBase()}/fetchInventoryReconciliationHistory`, {
      brandOwnerMosymphonyID,
      vendorMosymphonyID,
      ...request,
    });
    if (!res.data?.status) {
      throw new Error(res.data?.message || res.data?.errorMessage || "Failed to check the selected date.");
    }
    const list = Array.isArray(res.data.inventoryReconciliationHistory)
      ? res.data.inventoryReconciliationHistory
      : [];
    return {
      entries: list.filter(
        (item) => item && ("startDate" in item || "reportStartDate" in item || "reportEndDate" in item)
      ),
      sameReportMessage:
        res.data.sameReportMessage ||
        list.find((item) => item?.sameReportMessage)?.sameReportMessage ||
        null,
    };
  };

  // A resolution no longer stands once a date or the mode changes.
  useEffect(() => {
    setResolved(null);
    setResolveError(null);
  }, [mode, singleDate, fromDate, toDate]);

  /*
    The runs a report is generated for, earliest first: those the dates
    resolved to, once each. Where any date resolves to no run the user is asked
    to choose another date (the API's message says so), and nothing is
    generated.
  */
  const runsOf = (resolution) => {
    const entries = resolution?.entries || [];
    if (!entries.length || entries.some((entry) => !entry.collabrixReconciliationID)) return [];
    const byId = new Map();
    entries.forEach((entry) => {
      const timestamp = String(entry.reconciliationDateTimeStamp || "");
      if (!byId.has(entry.collabrixReconciliationID)) {
        byId.set(entry.collabrixReconciliationID, {
          id: entry.collabrixReconciliationID,
          timestamp,
          day: runDayOf(timestamp),
          time: timestamp.slice(11, 16),
        });
      }
    });
    return [...byId.values()].sort((a, b) => (a.timestamp < b.timestamp ? -1 : 1));
  };

  const selectedRuns = useMemo(() => runsOf(resolved), [resolved]);

  // Whether the API has something to tell the user about the dates.
  const hasMessages = (resolution) =>
    !!resolution?.sameReportMessage || (resolution?.entries || []).some((entry) => entry.errorMessage);

  /*
    Once the API has a message about the dates — another date's report used
    instead, beyond the 14-day limit, both dates on one report — or a date
    resolves to no report, Continue stays disabled until a date changes and
    the resolution is cleared.
  */
  const continueBlocked = !!resolved && (hasMessages(resolved) || selectedRuns.length === 0);

  // A run's name on its tab, button and file: the date, and the time as well
  // where more than one selected run falls on that day.
  const runLabel = (run) => {
    if (!run) return "";
    const sharesDay = selectedRuns.filter((r) => r.day === run.day).length > 1;
    return sharesDay && run.time ? `${formatDate(run.day)} ${run.time}` : formatDate(run.day);
  };

  /* ---- notices ---------------------------------------------------- */

  const notice = (key, head, body) => (
    <Box className={classes.notice} key={key}>
      <Typography className={classes.noticeHead}>{head}</Typography>
      {body && <Typography className={classes.noticeBody}>{body}</Typography>}
    </Box>
  );

  /*
    An API message as a notice: the text before the first "\n" in bold, the
    rest on the line below, e.g. "To — no report exists for 14-OCT-2026.\n
    13-OCT-2026 is the closest available date (1 days away) and will be used
    instead."
  */
  const messageNotice = (key, message) => {
    const [head, ...rest] = String(message).split("\n");
    return notice(key, head, rest.join("\n").trim() || null);
  };

  // The API's message for each date, and for both dates resolving to one report.
  const resolutionNotices = () => [
    ...(resolved?.entries || [])
      .filter((entry) => entry.errorMessage)
      .map((entry, i) => messageNotice(`entry-${i}`, entry.errorMessage)),
    resolved?.sameReportMessage ? messageNotice("same", resolved.sameReportMessage) : null,
  ];

  /* ---- report rows -------------------------------------------------- */

  const fetchReport = async (run) => {
    const response = await axios.post(`${apiBase()}/fetchInventoryReconciliationData`, {
      brandOwnerMosymphonyID,
      vendorMosymphonyID,
      vendorPlant: ["all"],
      collabrixReconciliationID: run.id,
      materialTypeID: ["all"],
      sponsorMaterialCode: "all",
      filterRowsWith: "all",
    });
    if (!response.data?.status) {
      throw new Error(response.data?.errorMessage || "Failed to load the report.");
    }
    const rows = Array.isArray(response.data.inventoryReconciliationData)
      ? response.data.inventoryReconciliationData
      : [];
    return [run.id, { rows: rows.filter((row) => !isZeroZeroRow(row)), cmoStatusColumns: cmoStatusColumnsOf(rows) }];
  };

  /*
    Continue. The first press sends the dates to
    fetchInventoryReconciliationHistory. Where every date resolves and there is
    nothing to tell the user, the report loads straight away. Where the API
    has messages, they are shown and the selection stays, with Continue
    disabled until a date changes.
  */
  const handleContinue = async () => {
    const request = historyRequest();
    if (!request) {
      setMissingDate(true);
      return;
    }
    setMissingDate(false);

    let resolution = resolved;
    if (!resolution) {
      setResolving(true);
      setResolveError(null);
      try {
        resolution = await resolveDates(request);
        setResolved(resolution);
      } catch (error) {
        console.error("Error checking the selected date:", error);
        setResolveError(error?.message || "Failed to check the selected date.");
        return;
      } finally {
        setResolving(false);
      }
      if (hasMessages(resolution)) return;
    }

    const runs = runsOf(resolution);
    if (runs.length === 0) return;
    loadReports(runs);
  };

  // The rows of each run, from fetchInventoryReconciliationData.
  const loadReports = async (runs) => {
    setActiveTab(0);
    setShowPreview(true);
    setReportsLoading(true);
    setReportsError(null);
    try {
      const loaded = await Promise.all(runs.map(fetchReport));
      setReports(Object.fromEntries(loaded));
    } catch (error) {
      console.error("Error loading custom report:", error);
      setReportsError(error?.message || "Failed to load the report.");
      setReports({});
    } finally {
      setReportsLoading(false);
    }
  };

  const reportFor = (run) => reports?.[run?.id] || { rows: [], cmoStatusColumns: [] };

  /* ---- columns ------------------------------------------------------ */

  /*
    The report carries the Reconciliation of Inventory columns, with the status
    breakup behind both totals after them: the four BMS statuses, then every
    status configured for the CMO.
  */
  const qtyColumn = (label, get) => ({
    label,
    numeric: true,
    value: (row) => formatReportQty(get(row)),
    amount: (row) => toAmount(get(row)),
  });

  /*
    A quantity the API sends ready formatted (cmoOnHandQtyDisplay,
    quantityDisplay), shown as it is given. In Excel a plain number stays a
    number, as written; one carrying a unit, e.g. "2000 (KG)", is text.
  */
  const displayColumn = (label, getDisplay) => ({
    label,
    numeric: true,
    asGiven: true,
    value: getDisplay,
    amount: (row) => toAmount(getDisplay(row)),
    text: (row) => (toAmount(getDisplay(row)) === undefined ? getDisplay(row) || undefined : undefined),
  });

  const columnsFor = (report) => [
    { label: "CMO ID", value: cmoIdCell },
    { label: "Plant ID", value: (row) => row.vendorPlant || "" },
    { label: "Brand", value: (row) => row.brand || "" },
    { label: "Material Type", value: (row) => row.materialType || "" },
    { label: "BMS Material Code / Description", value: materialCodeCell },
    { label: "BMS UoM", value: uomCell },
    { label: "BMS Batch Number", value: (row) => row.sponsorBatchNumber || "" },
    {
      label: "CMO Batch Number",
      value: (row) => cmoBatchesOf(row).map(cmoBatchNumberOf).filter(Boolean).join(", "),
    },
    { label: "BMS Expiry Date", value: (row) => formatReconDate(row.sponsorBatchExpiryDate) },
    {
      label: "CMO Expiry Date",
      value: (row) =>
        [
          ...new Set(
            cmoBatchesOf(row)
              .map((b) => formatReconDate(b?.expiryDate || b?.cmoBatchExpiryDate))
              .filter(Boolean)
          ),
        ].join(", "),
    },
    qtyColumn("BMS Total Qty.", (row) => row.sponsorOnHandQty),
    displayColumn("CMO Total Qty.", cmoQtyCell),
    ...BMS_STATUS_COLUMNS.map((col) => qtyColumn(col.label, (row) => row?.[col.key])),
    ...(report.cmoStatusColumns || []).map((col) =>
      displayColumn(col.label, (row) => cmoStatusDisplay(row, col.key))
    ),
    qtyColumn("Allocated Qty.", (row) => row.sponsorAllocatedQuantity),
    qtyColumn("Difference Between BMS Total Qty. And CMO Total Qty.", (row) => row.difference),
    { label: "Reason For Difference", value: (row) => row.reasonForDifference || "" },
  ];

  /* ---- download --------------------------------------------------- */

  /*
    One Excel file per resolved run — "Download both files" downloads both.
    Each file states the CMO and the date of the reconciliation it was
    generated from above the table.
  */
  const handleDownload = () => {
    selectedRuns.forEach((run) => {
      const report = reportFor(run);
      const columns = columnsFor(report);
      const HEADER_ROWS = 4; // title, report date, blank, column labels
      const sheet = XLSX.utils.aoa_to_sheet([
        [`Inventory Reconciliation — ${cmoName}`],
        [`Report date ${runLabel(run)}`],
        [],
        columns.map((col) => col.label),
        ...(report.rows || []).map((row) =>
          columns.map((col) => {
            if (!col.numeric) return col.value(row);
            const text = col.text && col.text(row);
            if (text) return text;
            const amount = col.amount(row);
            return amount === undefined ? "" : amount;
          })
        ),
      ]);
      // Quantities are numbers in the file, so they sum and sort, shown to
      // three decimals like the preview.
      (report.rows || []).forEach((_, r) => {
        columns.forEach((col, c) => {
          if (!col.numeric || col.asGiven) return;
          const cell = sheet[XLSX.utils.encode_cell({ r: r + HEADER_ROWS, c })];
          if (cell && cell.t === "n") cell.z = "0.000";
        });
      });
      const book = XLSX.utils.book_new();
      // Excel does not allow ":" in a sheet name.
      const name = runLabel(run).replace(/:/g, ".");
      XLSX.utils.book_append_sheet(book, sheet, name.slice(0, 31));
      XLSX.writeFile(book, `Inventory Reconciliation - ${cmoName} - ${name}.xlsx`);
    });
  };

  /* ---- preview ---------------------------------------------------- */

  const renderPreview = () => {
    const run = selectedRuns[Math.min(activeTab, selectedRuns.length - 1)];
    const report = reportFor(run);
    const columns = columnsFor(report);
    const rows = report.rows || [];

    return (
      <Box>
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "20px",
            gap: "16px",
          }}
        >
          <Typography
            className={classes.backLink}
            onClick={() => setShowPreview(false)}
          >
            ‹ Back to selection
          </Typography>
          <Typography variant="h5" style={{ fontWeight: 600 }}>
            Generate Custom Report
          </Typography>
          <Button
            variant="contained"
            startIcon={<GetApp />}
            className={classes.downloadButton}
            onClick={handleDownload}
            disabled={reportsLoading || !!reportsError}
          >
            {selectedRuns.length > 1 ? "Download both files" : `Download ${runLabel(run)}`}
          </Button>
        </Box>

        {/* A tab for each resolved date, the earlier one selected on opening. */}
        {selectedRuns.length > 1 && (
          <Box className={classes.segment} style={{ marginBottom: "16px" }}>
            {selectedRuns.map((r, i) => (
              <Button
                key={r.id}
                className={`${classes.segmentButton} ${i === activeTab ? classes.segmentOn : classes.segmentOff}`}
                onClick={() => setActiveTab(i)}
              >
                {runLabel(r)}
              </Button>
            ))}
          </Box>
        )}

        <Box className={classes.previewCard}>
          <Box className={classes.previewHead}>
            <Typography style={{ fontWeight: 500 }}>
              {`Inventory Reconciliation — ${cmoName}`}
            </Typography>
            <Typography variant="body2" style={{ color: "#757575" }}>
              {`Report date ${runLabel(run)}`}
            </Typography>
          </Box>

          <TableContainer component={Paper} elevation={0} style={{ maxHeight: 360 }}>
            {/*
              The header stays on scrolling down and the first column on
              scrolling across. MUI's stickyHeader sets left: 0 on every header
              cell, which stacks them on a horizontal scroll, so only the first
              one keeps it.
            */}
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {columns.map((col, c) => (
                    <TableCell
                      key={col.label}
                      align={col.numeric ? "right" : "left"}
                      style={{
                        whiteSpace: "nowrap",
                        fontWeight: 500,
                        backgroundColor: "#fafafa",
                        ...(c === 0
                          ? { left: 0, zIndex: 3, borderRight: "1px solid #e0e0e0" }
                          : { left: "auto" }),
                      }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {reportsLoading ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} style={{ padding: "32px" }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : reportsError || rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} style={{ padding: "32px" }}>
                      <Typography variant="body2" color={reportsError ? "error" : "textSecondary"}>
                        {reportsError || "No rows for this reconciliation."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row, i) => (
                    <TableRow key={`${row.collabrixBatchReconciliationID}-${i}`} className={classes.reportRow}>
                      {columns.map((col, c) => (
                        <TableCell
                          key={col.label}
                          align={col.numeric ? "right" : "left"}
                          className={c === 0 ? classes.stickyFirstCell : undefined}
                          style={{ whiteSpace: "nowrap" }}
                        >
                          {col.value(row)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box className={classes.previewFoot}>
            {`${rows.length} row(s) · ${(report.cmoStatusColumns || []).filter((col) => col.partOfCMOTotalQty).length} status column(s) summing to CMO total qty. · rows with both quantities zero are excluded.`}
          </Box>
        </Box>
      </Box>
    );
  };

  /* ---- selection -------------------------------------------------- */

  /*
    Shown in the user's date format (selectedDateFormat), as the other date
    fields are; a native date input would follow the browser's locale instead.
    The value is still kept as yyyy-mm-dd, which is what the API is sent.
  */
  const dateFormat = localStorage.selectedDateFormat;

  const dateField = (value, setValue) => (
    <MuiPickersUtilsProvider utils={MomentUtils}>
      <KeyboardDatePicker
        autoOk
        disableToolbar
        variant="inline"
        inputVariant="outlined"
        size="small"
        fullWidth
        format={dateFormat}
        placeholder={dateFormat}
        value={value ? moment(value, "YYYY-MM-DD") : null}
        onChange={(date) => {
          // A date still being typed is no date yet.
          setValue(date && date.isValid() ? date.format("YYYY-MM-DD") : "");
          setMissingDate(false);
        }}
      />
    </MuiPickersUtilsProvider>
  );

  const renderSelection = () => (
    <Box>
      <Typography variant="h5" style={{ fontWeight: 600, textAlign: "center", marginBottom: "28px" }}>
        Generate Custom Report
      </Typography>

      <Typography className={classes.fieldLabel}>Report for</Typography>
      <Box className={classes.segment} style={{ marginBottom: "24px" }}>
        <Button
          className={`${classes.segmentButton} ${mode === "day" ? classes.segmentOn : classes.segmentOff}`}
          onClick={() => {
            setMode("day");
            setMissingDate(false);
          }}
        >
          A single day
        </Button>
        <Button
          className={`${classes.segmentButton} ${mode === "period" ? classes.segmentOn : classes.segmentOff}`}
          onClick={() => {
            setMode("period");
            setMissingDate(false);
          }}
        >
          A time period
        </Button>
      </Box>

      {mode === "day" ? (
        <Box style={{ marginBottom: "20px" }}>
          <Typography className={classes.fieldLabel}>Select date</Typography>
          {dateField(singleDate, setSingleDate)}
        </Box>
      ) : (
        <Box style={{ display: "flex", gap: "24px", marginBottom: "20px" }}>
          <Box style={{ flex: 1 }}>
            <Typography className={classes.fieldLabel}>From</Typography>
            {dateField(fromDate, setFromDate)}
          </Box>
          <Box style={{ flex: 1 }}>
            <Typography className={classes.fieldLabel}>To</Typography>
            {dateField(toDate, setToDate)}
          </Box>
        </Box>
      )}

      <Box style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "24px" }}>
        {resolveError && notice("resolve-error", resolveError)}
        {missingDate && notice("missing", "Select a date to continue.")}
        {!resolving && resolutionNotices()}
      </Box>

      <Box style={{ display: "flex", justifyContent: "center" }}>
        <Button
          className={classes.continueButton}
          endIcon={resolving ? <CircularProgress size={16} style={{ color: "#fff" }} /> : <NavigateNext />}
          disabled={resolving || continueBlocked}
          onClick={handleContinue}
        >
          Continue
        </Button>
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      maxWidth={showPreview ? "lg" : "sm"}
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogContent style={{ padding: "24px 32px 32px" }}>
        <Box style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
          <Typography style={{ fontWeight: 500 }}>Cancel</Typography>
          <IconButton size="small" onClick={onClose} aria-label="close">
            <CloseIcon style={{ color: "#e53935" }} />
          </IconButton>
        </Box>
        <Box style={{ position: "relative" }}>
          {showPreview ? renderPreview() : renderSelection()}

          {/* While fetchInventoryReconciliationHistory checks the selected
              date(s), the selection waits under a loader. Cancel above stays
              usable. */}
          {resolving && !showPreview && (
            <Box className={classes.loadingOverlay}>
              <CircularProgress size={32} />
              <Typography variant="body2" style={{ marginTop: "12px", color: "#757575" }}>
                Loading Report Data…
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default CustomReportDialog;
